import { CompareUtil } from "@base/util/CompareUtil";
import { calcKeyJson, intern } from "@base/util/Interned";
import { GetTaggedType } from "@base/util/TypeUtil";
import { ConfiguratorType } from "@biz/Definitions";
import { DealerType, DealerTypes } from "@biz/definitions/AccessDefinitions";
import { Product } from "@biz/definitions/Product";
import { SST } from "@biz/definitions/SST";
import { sailTypesMap, SunSailShapeType, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { getSunSailNameShort } from "@biz/definitions/SunSailDefinitions.util";
import { RaffType } from "@biz/items/raff/RaffDefinitions";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { ILocaContext, LocaContext } from "@biz/language/LocaContext";
import { AccessHelper } from "@biz/logic/AccessHelper";
import { AppUser, IAppUser } from "@biz/models/AppUser";
import { ProductData } from "@biz/projectCalculation/ProjectDefinitions";
import { SCProductData } from "@biz/sc/IShopData";
import {
    ICurtainDescriptor, IExtendedProductDescriptor, ILoungeDescriptor, IModelProductDescriptor, INetDescriptor, IProductCategorySelectionInfo, IProductDescriptor, IProductSelectionInfo, IRaffDescriptor, IStandardCollectionDescriptor,
    ISunSailDescriptor
} from "./ProductDescriptor";
import pick from "lodash/pick";

import type { ConfiguratorItemData } from "@biz/definitions/IVariantData3d";
import type { LanguageId } from "@biz/language/Languages";
export class PD {
  static isSail(pd: IProductDescriptor | undefined): pd is ISunSailDescriptor {
    if (!pd || !("pdt" in pd))
      return PD.#detectSail(pd);
    return pd.pdt === "sunSail";
  }

  static isRaff(pd: IProductDescriptor | undefined): pd is IRaffDescriptor {
    if (!pd || !("pdt" in pd))
      return PD.#detectRaff(pd);
    return pd.pdt === "raff";
  }

  static isSailOrRaff(pd: IProductDescriptor | undefined): pd is (ISunSailDescriptor | IRaffDescriptor) {
    return PD.isSail(pd) || PD.isRaff(pd);
  }

  static isLounge(pd: IProductDescriptor | undefined): pd is ILoungeDescriptor {
    if (!pd || !("pdt" in pd))
      return PD.#detectLounge(pd);
    return pd.pdt === "lounge";
  }

  static isModelProduct(pd: IProductDescriptor | undefined): pd is IModelProductDescriptor {
    if (!pd || !("pdt" in pd))
      return PD.#detectModelProduct(pd);
    return pd.pdt === "modelProduct";
  }

  static isNet(pd: IProductDescriptor | undefined): pd is INetDescriptor {
    return pd?.pdt === "net"
  }

  static isLoungeOrLoungeModel(pd: IProductDescriptor | undefined): pd is (ILoungeDescriptor | IModelProductDescriptor) {
    return PD.isLounge(pd) || PD.isModelProduct(pd)
  }

  static isCurtain(pd: IProductDescriptor | undefined): pd is ICurtainDescriptor {
    if (!pd || !("pdt" in pd))
      return PD.#detectCurtain(pd);
    return pd.pdt === "curtain";
  }

  static isStandardCollection(pd: IProductDescriptor): pd is IStandardCollectionDescriptor {
    if (!pd || !("pdt" in pd))
      return false;
    return pd.pdt === "standardCollection";
  }
  static equals(a: IProductDescriptor, b: IProductDescriptor, options?: { ignoreSandyPoleCount?: boolean }): boolean {
    const ignoreList: string[] | undefined = options?.ignoreSandyPoleCount && PD.isSail(a) && SST.is(a.sunSailType, SST.Sandy) ? ["poleCount"] : undefined;

    return CompareUtil.shallowEqualObjectsWithIgnore(PD.#simplify(a), PD.#simplify(b), { ignoreList });
  }

  static productToExtendedPD(product: ProductData): IExtendedProductDescriptor {
    return {
      pd: PD.productToPD(product),
      count: product.count,
      orderType: Product.getOrderType(product)
    }
  }

  static interned(pd: IProductDescriptor): IProductDescriptor {
    return intern(pd);
  }

  static toSunSailType(pd: IProductDescriptor): SunSailType {
    if (PD.isSail(pd))
      return pd.sunSailType;
    else if (PD.isRaff(pd)) {
      switch (pd.raffType) {
        case RaffType.C:
          return SunSailType.RaffC;
        case RaffType.M_Guide:
          return SunSailType.RaffM;
        case RaffType.M_Rope:
          return SunSailType.RaffM;
      }
    } else if (PD.isCurtain(pd)) {
      return SunSailType.Curtain;
    } else if (PD.isLounge(pd)) {
      return SunSailType.Lounge;
    } else if (PD.isModelProduct(pd)) {
      return SunSailType.ModelProduct;
    } else if (PD.isStandardCollection(pd)) {
      return SunSailType.StandardCollection;
    } else if (PD.isNet(pd)) {
      return SunSailType.Net;
    }
    throw new Error("Invalid toSunSailType!");
  }

  static productToSunSailType(product: ProductData | ConfiguratorItemData | SCProductData): SunSailType {
    if (!product)
      return SunSailType.None;
    const sunSailType = (product as any).sunSailType;
    if (sunSailType !== undefined) { // shortcut
      return sunSailType;
    }
    return PD.toSunSailType(PD.productToPD(product, false));
  }

  static productToPoleCount(product: ProductData | ConfiguratorItemData | SCProductData): number {
    const count = (product as any).poleCount || (product as any).poles?.length;
    if (count !== undefined)
      return count;
    const pd = PD.productToPD(product);
    if (pd.pdt === "sunSail")
      return pd.poleCount;
    return 0;
  }

  static productToPD(product: ProductData | ConfiguratorItemData | SCProductData, intern: boolean = true): IProductDescriptor {
    const pd = ("pd" in product) ? product.pd : PD.#productToPD(product);
    if (!intern)
      return pd;
    return PD.interned(pd);
  }

  static typedProduct<T extends IProductDescriptor["pdt"]>(product: ProductData | ConfiguratorItemData | SCProductData, type: T): GetTaggedType<IProductDescriptor, { pdt: "sunSail" }> {
    const pd = PD.productToPD(product);
    if (pd.pdt !== type)
      throw Error("productToTyped invalid type: " + pd.pdt + " vs " + type)
    return pd as any;
  }

  static typed<T extends IProductDescriptor["pdt"]>(pd: IProductDescriptor, type: T): GetTaggedType<IProductDescriptor, { pdt: T }> {
    if (pd.pdt !== type)
      throw Error("productToTyped invalid type: " + pd.pdt + " vs " + type)
    return pd as any;
  }

  static #productToPD(product: ProductData | ConfiguratorItemData): IProductDescriptor {
    if ("itemType" in product) {
      switch (product.itemType) {
        case "sunSail": return { pdt: "sunSail", sunSailType: product.sunSailType, poleCount: Poles.getCount(product, "corner"), version: product.version }
        case "raff": return { pdt: "raff", raffType: product.raffType, version: product.version, sailMount: product.sailMount }
        case "curtain": return { pdt: "curtain", curtainType: product.curtainType, version: product.version }
        case "lounge": return { pdt: "lounge", loungeType: product.loungeType, version: product.version }
        case "modelProduct": return { pdt: "modelProduct", templateId: product.templateId, version: product.version }
        case "net": return { pdt: "net", version: product.version, shapeType: product.shapeType }
        // non products
        case "model": return { pdt: "model", itemType: "model", templateId: product.templateId, version: product.version }
        case "group": return { pdt: "group", itemType: "group", version: "2024" }
        case "ground":
        case "wall":
        case "fence":
        case "roof":
        case "opening":
        case "sandbox":
          return { pdt: "generic", itemType: product.itemType, version: product.version }
      }
    }
    if (Product.isProduct2d(product)) {
      switch (product.productType) {
        case "sunSail2d": return { pdt: "sunSail", sunSailType: product.sunSailType, poleCount: product.poleCount, version: product.version }
        case "curtain2d": return { pdt: "curtain", curtainType: product.curtainType, version: product.version };
        case "standardCollection2d": return { pdt: "standardCollection", version: product.version }
      }
      throw new Error("productToPD (2d) not detected" + JSON.stringify(product))
    }
    throw new Error("productToPD not detected" + JSON.stringify(product))
  }

  static _getProductNameId(pd: IProductDescriptor): { id: LanguageId, keys?: Record<string, any>, ids?: Record<string, string> } {
    if ("pdt" in pd) {
      if (pd.pdt === "standardCollection")
        return { id: LocaContext.connect("Item_Name", pd.pdt) }
    }
    if ("itemType" in pd) {
      switch (pd.itemType) {
        // case "modelProduct": return { id: LocaContext.connect("Model_Name", pd.templateId) }
        case "model": return { id: LocaContext.connect("Model_Name", pd.templateId) }
        case "ground":
        case "roof":
        case "wall":
        case "fence":
        case "sandbox":
        case "opening":
        case "shoppingCart":
        case "group":
        case "undefined":
          return { id: LocaContext.connect("Item_Name", pd.itemType) }
      }
      pd = PD.productToPD(pd);
    }
    if (PD.#detectSail(pd)) {
      const sunSailShape = SST.getSunSailShape(pd.sunSailType, pd.poleCount);
      const keys: { sunSailType: SunSailType, sunSailShapeType?: SunSailShapeType } = { sunSailType: pd.sunSailType, sunSailShapeType: sunSailShape };
      if (sunSailShape)
        keys.sunSailShapeType = sunSailShape;
      const id = LocaContext.connect("Product_SunSail", SunSailType[pd.sunSailType]);
      return {
        id,
        keys,
        ids: {
          sunSailType: id,
          shape: sunSailShape ? LocaContext.connect("Product_SunSail_Shape", sunSailShape) : ""
        }
      }
    } else if (PD.#detectRaff(pd)) {
      return { id: LocaContext.connect("Product_Raff", RaffType[pd.raffType]) }
    } else if (PD.#detectCurtain(pd)) {
      return { id: LocaContext.connect("Product_Curtain", pd.curtainType) };
    } else if (PD.#detectLounge(pd)) {
      return { id: LocaContext.connect("Product_Lounge", pd.loungeType) }
    } else if (PD.#detectModelProduct(pd)) {
      return { id: LocaContext.connect("Product_Model", pd.templateId) }
    } else if (PD.isNet(pd))
      return { id: LocaContext.connect("Product_Net"), keys: { netShapeType: pd.shapeType } }
    if (pd) {
      console.warn("ProductDescriptor: ", pd);
    }
    throw new Error("Invalid product descriptor: " + JSON.stringify(pd));
  }

  static #simplify(pd: IProductDescriptor): IProductDescriptor {
    if (PD.#detectSail(pd)) {
      return pick(pd, "pdt", "sunSailType", "poleCount", "version")
    } else if (PD.#detectRaff(pd)) {
      return pick(pd, "pdt", "raffType", "version", "sailMount")
    } else if (PD.#detectCurtain(pd)) {
      return pick(pd, "pdt", "curtainType", "version")
    } else if (PD.#detectLounge(pd)) {
      return pick(pd, "pdt", "loungeType", "version")
    } else if (PD.#detectModelProduct(pd)) {
      return pick(pd, "pdt", "templateId", "version")
    } else if (PD.isStandardCollection(pd)) {
      return pick(pd, "pdt", "version")
    }
    throw new Error("Invalid product type!");
  }

  static getProductKey(pd: IProductDescriptor): string {
    /*const ret = PD._getProductNameId(pd);
    if (!ret.keys)
      return ret.id;*/
    return calcKeyJson(pd);
  }

  static getProductNameString(pd: IProductDescriptor, lc: ILocaContext): string {
    const ret = PD._getProductNameId(pd);
    if (ret.ids) {
      const parts: string[] = [];
      for (const k of Object.keys(ret.ids)) {
        if (ret.ids[k]) {
          parts.push(lc.t(ret.ids[k] as LanguageId));
        }
      }
      return parts.join(" ");
    } else {
      return lc.t(ret.id);
    }
  }

  //* deprecated */
  static #detectSail(pd: IProductDescriptor | undefined): pd is ISunSailDescriptor {
    if (!pd || !("sunSailType" in pd))
      return false;
    if (!SST.isSail(pd.sunSailType))
      return false;
    if ("poleCount" in pd && !!pd.poleCount)
      return true;
    throw new Error("No poleCount in sunSail!");
  }
  /** @deprecated */
  static #detectRaff(pd: IProductDescriptor | undefined): pd is IRaffDescriptor {
    return !!pd && "raffType" in pd && pd.raffType !== undefined && pd.raffType !== null && pd.raffType !== RaffType.None;
  }
  /** @deprecated */
  static #detectLounge(pd: IProductDescriptor | undefined): pd is ILoungeDescriptor {
    return !!pd && "loungeType" in pd && pd.loungeType !== undefined && pd.loungeType !== null;
  }
  /** @deprecated */
  static #detectModelProduct(pd: IProductDescriptor | undefined): pd is IModelProductDescriptor {
    return !!pd && "templateId" in pd && pd.templateId !== undefined && pd.templateId !== null;
  }
  /** @deprecated */
  static #detectCurtain(pd: IProductDescriptor | undefined): pd is ICurtainDescriptor {
    return !!pd && "curtainType" in pd && pd.curtainType !== undefined && pd.curtainType !== null;
  }

  static isOrderableByDealer(pd: IProductDescriptor, dealerType: DealerType | undefined): boolean {
    if (!dealerType)
      return false;
    const dt = PD.getDealerTypesForProduct(pd);
    return dt.includes(dealerType);
  }

  static getDealerTypesForProduct(pd: IProductDescriptor): DealerTypes[] {
    if (PD.isSail(pd)) {
      return sailTypesMap[pd.sunSailType]?.isSail === "RS" ? [DealerType.RS] : [DealerType.RS, DealerType.SO];
    } else if (PD.isRaff(pd)) {
      return [DealerType.RS, DealerType.SO]
    } else if (PD.isCurtain(pd)) {
      return [DealerType.RS, DealerType.SO]
    } else if (PD.isStandardCollection(pd)) {
      return [DealerType.RS, DealerType.SO];
    } else if (PD.isLounge(pd) || PD.isModelProduct(pd)) {
      return [DealerType.RS, DealerType.SO, DealerType.SL]
    } else if (PD.isNet(pd)) {
      return [DealerType.RS, DealerType.SO, DealerType.SL]
    }
    return [];
  }

  static isOrderableProduct(pd: IProductDescriptor): boolean {
    return PD.getDealerTypesForProduct(pd).length > 0;
  }

  static getNameShort(pd: IProductDescriptor): string {
    if (PD.isSail(pd)) {
      return getSunSailNameShort(pd.sunSailType) + (pd.poleCount ? "" + pd.poleCount : "");
    }
    const sunSailType = PD.toSunSailType(pd);
    return getSunSailNameShort(sunSailType);
  }
}

export function getProductsForCategory(cat: IProductCategorySelectionInfo, appUser: IAppUser | undefined, configuratorType: ConfiguratorType, orderableOnly: boolean): IProductSelectionInfo[] {
  if (!appUser)
    return [];

  if (cat.access && !AccessHelper.hasAccess(appUser, cat.access)) {
    return [];
  }
  const products = cat.items.filter((item) => {
    const accessOrBoolean = item.filter[configuratorType]
    if (typeof accessOrBoolean === "number") {
      if (!AccessHelper.hasAccess(appUser, accessOrBoolean)) {
        return false;
      }
    } else {
      if (!accessOrBoolean)
        return false;
    }
    if (orderableOnly && !PD.isOrderableByDealer(item.pd, AppUser.getDealerType(appUser))) {
      return false;
    }
    return true;
  })
  return products;
}